import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { KeyPoint, Section } from './LegalPage';
import { LEGAL_ENTITY } from '../../config/legal';

const SECTIONS = [
  { id: 'who-we-are', title: 'Who we are' },
  { id: 'what-we-collect', title: 'What we collect' },
  { id: 'what-we-never-see', title: 'What we never see' },
  { id: 'how-we-use-it', title: 'How we use it' },
  { id: 'measurement', title: 'Measurement and cookies' },
  { id: 'processors', title: 'Who we share it with' },
  { id: 'ai', title: 'AI features' },
  { id: 'retention', title: 'How long we keep it' },
  { id: 'your-rights', title: 'Your rights' },
  { id: 'security', title: 'Security' },
  { id: 'children', title: 'Children' },
  { id: 'transfers', title: 'International transfers' },
  { id: 'changes', title: 'Changes' },
  { id: 'contact', title: 'Contact' },
];

export default function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      summary="What Guhso collects, why, who else touches it, and how to get it back or get rid of it. Written to describe what the site actually does rather than every possibility."
      sections={SECTIONS}
    >
      <Section id="who-we-are" title="Who we are" index="1">
        <p>
          {LEGAL_ENTITY.name} operates <strong>{LEGAL_ENTITY.site}</strong>: a podcast and
          publishing site with a merchandise shop, paid memberships, donations, and a
          business directory that sells advertising placements.
        </p>
        <p>
          For the purposes of data protection law, {LEGAL_ENTITY.name} is the controller of the
          personal information described here. Our registered address is{' '}
          <span className="legal-todo">{LEGAL_ENTITY.address}</span>.
        </p>
      </Section>

      <Section id="what-we-collect" title="What we collect" index="2">
        <p>We collect only what a given interaction needs. Browsing the site requires no account.</p>

        <h3>Account</h3>
        <p>
          Your name and email address. We sign you in with a one-time code sent to your email
          rather than a password, so we do not hold a password for your account.
        </p>

        <h3>Shop orders</h3>
        <p>
          Name, email address, phone number, and the shipping address you provide. We also store
          the order contents, amounts, and a payment reference and receipt link returned by our
          payment processor.
        </p>

        <h3>Memberships and donations</h3>
        <p>
          Name, email address, and the subscription or donation record — amount, currency,
          interval, status, and renewal dates.
        </p>

        <h3>Advertisers</h3>
        <p>
          Name, email address, an optional phone number, your campaign and its dates, and the
          listing content you supply.
        </p>

        <h3>Mailing list</h3>
        <p>
          Your email address, and the fact and time of your confirmation. We use confirmed
          opt-in: subscribing sends you an email you must act on before we send anything else.
        </p>

        <h3>Shorts engagement</h3>
        <p>
          Likes and shares use a random browser identifier so they can be counted without an
          account. We store only a one-way keyed hash of that identifier. Comments require a
          signed-in account and are stored with your account identifier, display name, and comment
          text. Your email address is never displayed with a comment.
        </p>

        <h3>Support and correspondence</h3>
        <p>Whatever you choose to put in a message to us.</p>
      </Section>

      <Section id="what-we-never-see" title="What we never see" index="3">
        <KeyPoint tone="good">
          <p>
            <strong>We never receive your full card number.</strong> Card details are captured by
            a secure field hosted by our payment processor and exchanged for a single-use token
            in your browser. The card number does not reach a Guhso server, and we store only the
            processor's reference and receipt link.
          </p>
        </KeyPoint>
        <KeyPoint tone="good">
          <p>
            <strong>We do not store IP addresses against advertising measurement.</strong> When we
            count that a directory listing was seen, we record a one-way keyed hash of a
            short-lived session signal instead of your IP address. It lets us avoid counting the
            same person twice within an hour; it cannot be reversed into an identity.
          </p>
        </KeyPoint>
        <p>
          We do not sell personal information, we do not share it with data brokers, and we do not
          run third-party advertising or profiling trackers on the site.
        </p>
      </Section>

      <Section id="how-we-use-it" title="How we use it" index="4">
        <div className="legal-table-wrap">
          <table>
            <thead>
              <tr><th>Purpose</th><th>Information used</th><th>Why we may do it</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Fulfil an order, membership, donation, or ad campaign</td>
                <td>Contact, address, payment reference</td>
                <td>Performance of our contract with you</td>
              </tr>
              <tr>
                <td>Sign you in and secure your account</td>
                <td>Email, one-time codes, session records</td>
                <td>Performance of our contract; our legitimate interest in security</td>
              </tr>
              <tr>
                <td>Send order, renewal, and service messages</td>
                <td>Name, email</td>
                <td>Performance of our contract</td>
              </tr>
              <tr>
                <td>Send the newsletter</td>
                <td>Email</td>
                <td>Your consent, withdrawable at any time</td>
              </tr>
              <tr>
                <td>Report campaign performance to advertisers</td>
                <td>Aggregated, non-identifying counts</td>
                <td>Our legitimate interest in operating the directory</td>
              </tr>
              <tr>
                <td>Display and count Shorts engagement</td>
                <td>Hashed browser identifier, share channel, account identifier, display name, comment</td>
                <td>Our legitimate interest in operating community features and preventing duplicate likes</td>
              </tr>
              <tr>
                <td>Keep records for tax and accounting</td>
                <td>Transaction records</td>
                <td>Legal obligation</td>
              </tr>
              <tr>
                <td>Prevent fraud and abuse</td>
                <td>Transaction and access records</td>
                <td>Our legitimate interest in protecting the service</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Where we rely on consent, you can withdraw it at any time without affecting anything we
          did before you withdrew it.
        </p>
      </Section>

      <Section id="measurement" title="Measurement and cookies" index="5">
        <p>We use a small number of cookies, and none of them are advertising cookies.</p>
        <ul>
          <li><strong>Session cookie</strong> — keeps you signed in and protects forms against cross-site request forgery. Required for the site to work.</li>
          <li><strong>Cart and preference storage</strong> — held in your browser so your basket and theme survive a page reload.</li>
        </ul>
        <p>
          Directory advertising measurement counts impressions, clicks, and contact actions such
          as tapping a phone number. As described above, these counts are tied to a keyed hash
          rather than to you, and advertisers receive totals only — never a list of people.
        </p>
      </Section>

      <Section id="processors" title="Who we share it with" index="6">
        <p>
          We share personal information only with service providers who process it on our
          instructions, and only what each needs.
        </p>
        <dl>
          <dt>Payment processing — Square and Stripe</dt>
          <dd>Card capture, charges, subscriptions, and receipts. They act as independent controllers for payment data under their own privacy notices.</dd>

          <dt>Hosting and email delivery</dt>
          <dd>Our hosting provider stores the site and database. Transactional and newsletter email is delivered by our email provider.</dd>

          <dt>Google / YouTube</dt>
          <dd>Where an administrator connects our YouTube channel, Google provides channel and viewing statistics about <em>our own</em> channel. This does not give us personal information about you.</dd>

          <dt>AI providers</dt>
          <dd>See the next section.</dd>
        </dl>
        <p>
          We may also disclose information where the law requires it, or to establish or defend
          legal claims. If the business is ever sold or reorganised, information may transfer with
          it, and this policy would continue to apply until you are told otherwise.
        </p>
      </Section>

      <Section id="ai" title="AI features" index="7">
        <p>
          Our advertising tools can draft copy, generate artwork, and produce voiceovers. To do
          that, the text and images an administrator submits are sent to third-party AI providers —
          currently OpenAI, Anthropic, Google (Gemini), and ElevenLabs.
        </p>
        <KeyPoint>
          <p>
            These features operate on <strong>advertising and editorial content</strong>. We do not
            submit customer records, order histories, addresses, or payment details to any AI
            provider.
          </p>
        </KeyPoint>
        <p>
          We use these providers' business and API offerings, under terms which provide that
          submitted content is not used to train their general models. Each provider's own privacy
          terms govern their handling of that content.
        </p>
      </Section>

      <Section id="retention" title="How long we keep it" index="8">
        <ul>
          <li><strong>Order, donation, membership, and advertising records</strong> — kept for as long as tax and accounting law requires, typically seven years from the transaction.</li>
          <li><strong>Account records</strong> — kept while your account is open, and removed on request.</li>
          <li><strong>Newsletter subscription</strong> — kept until you unsubscribe, plus a suppression record so we do not email you again by accident.</li>
          <li><strong>One-time sign-in codes</strong> — expire within minutes.</li>
          <li><strong>Advertising measurement</strong> — retained as aggregate counts; the session hash has no value once the deduplication window has passed.</li>
          <li><strong>Shorts engagement</strong> — comments remain while the Short is published or until removed; like and share records are deleted when the Short is deleted.</li>
        </ul>
      </Section>

      <Section id="your-rights" title="Your rights" index="9">
        <p>
          Subject to the law that applies to you, you can ask us to give you a copy of your
          information, correct it, delete it, restrict or object to how we use it, provide it in a
          portable format, or withdraw consent.
        </p>
        <p>
          Email <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`}>{LEGAL_ENTITY.privacyEmail}</a>. We
          reply within 30 days. We will ask you to confirm your identity first, and we may need to
          keep records the law requires us to retain even after other information is deleted.
        </p>
        <p>
          Every newsletter contains a one-click unsubscribe link. You can cancel a membership or
          recurring donation at any time from your profile, or by emailing us.
        </p>
        <p>
          If you think we have handled your information badly, please tell us first — but you are
          entitled to complain to your local data protection authority.
        </p>
      </Section>

      <Section id="security" title="Security" index="10">
        <p>
          The site is served over HTTPS. Payment credentials and third-party API keys are
          encrypted at rest, administrative areas require an authenticated administrator account,
          and card data never reaches our servers.
        </p>
        <p>
          No system is perfectly secure. If a breach affects your personal information and creates
          a real risk to you, we will notify you and the relevant regulator as the law requires.
        </p>
      </Section>

      <Section id="children" title="Children" index="11">
        <p>
          Guhso is intended for adults. We do not knowingly collect personal information from
          children under 13, and we do not target the site at them. If you believe a child has
          given us information, contact us and we will delete it.
        </p>
      </Section>

      <Section id="transfers" title="International transfers" index="12">
        <p>
          Our hosting and several of our providers operate in the United States, so your
          information may be processed outside the country where you live. Where the law requires
          a transfer safeguard, we rely on our providers' standard contractual clauses or an
          equivalent mechanism.
        </p>
      </Section>

      <Section id="changes" title="Changes" index="13">
        <p>
          We update this policy when what we do changes. The effective date at the top always
          reflects the current version, and we will give prominent notice of any change that
          materially reduces your rights.
        </p>
      </Section>

      <Section id="contact" title="Contact" index="14">
        <p>
          Privacy questions: <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`}>{LEGAL_ENTITY.privacyEmail}</a>
          <br />
          Anything else: <a href={`mailto:${LEGAL_ENTITY.contactEmail}`}>{LEGAL_ENTITY.contactEmail}</a>
          <br />
          Post: <span className="legal-todo">{LEGAL_ENTITY.address}</span>
        </p>
        <p>
          See also our <Link to="/terms">Terms of Service</Link> and{' '}
          <Link to="/returns">Returns &amp; Shipping Policy</Link>.
        </p>
      </Section>
    </LegalPage>
  );
}
